import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import InfoIcon from "../../../../components/icons/InfoIcon";
import { CellHeartbeat } from "../../../../context/hotspots/HotspotsProvider";
import classNames from "../../../../utils/classNames";

interface Props {
  cellHotspot: CellHeartbeat;
}

const CellStatus: React.FC<Props> = ({ cellHotspot }) => {
  const status = useMemo(() => {
    if (!cellHotspot?.timestamp) {
      return "Not Available";
    }

    if (isRadioActive(cellHotspot)) {
      return "Active";
    }

    return "Inactive";
  }, [cellHotspot]);

  const operationMode = useMemo(() => {
    if (!cellHotspot) {
      return "Unknown";
    }

    if (cellHotspot.operationMode) {
      return "On Air";
    }

    return "Offline";
  }, [cellHotspot]);

  const category = useMemo(() => {
    switch (cellHotspot?.cbsdCategory) {
      case "A":
        return "Indoor";
      case "B":
        return "Outdoor";
      default:
        return "Unknown";
    }
  }, [cellHotspot?.cbsdCategory]);

  const last4OfSerial = useMemo(() => {
    if (!cellHotspot?.cbsdId) return "";

    const length = cellHotspot.cbsdId.length;

    return cellHotspot.cbsdId.slice(length - 4, length);
  }, [cellHotspot?.cbsdId]);

  return (
    <div className="flex w-full flex-col rounded-lg dark:bg-gray-800 p-3 py-4 font-medium shadow-md">
      <div className="flex flex-col">
        <div className="flex items-center">
          <span className="pr-2 text-xl">{`Radio ${last4OfSerial}`}</span>
          <span className="text-white text-sm px-2.5 bg-green-500 flex items-center rounded-full">
            {status}
          </span>
          <Tooltip
            overlay={
              "A radio is active if it has a heartbeat in the last 24 hours and an 'On Air' operation mode."
            }
          >
            <InfoIcon className="w-4 dark:text-gray-500 ml-1" />
          </Tooltip>
        </div>
      </div>
      <div className="my-4 h-px bg-gray-400" />
      <div className="flex flex-row flex-wrap justify-between">
        <div className="mb-1 flex flex-col">
          <span className="text-sm font-medium text-gray-500">Type</span>
          <span className="mt-1 text-sm text-gray-900 dark:text-white">
            {category}
          </span>
        </div>
        <div className="mb-1 flex flex-col">
          <span className="flex text-sm font-medium text-gray-500">
            Last Heartbeat
          </span>
          <span className="mt-1 text-sm text-gray-900 dark:text-white">
            {cellHotspot.timestamp === undefined
              ? "Unknown"
              : moment.unix(cellHotspot.timestamp).fromNow()}
          </span>
        </div>
        <div className="mb-1 flex flex-col">
          <span className="flex text-sm font-medium text-gray-500">
            Operation Mode
            <Tooltip
              overlay={
                "Check your radio's local dashboard for more information."
              }
            >
              <InfoIcon className="w-4 dark:text-gray-500 ml-1" />
            </Tooltip>
          </span>
          <span
            className={classNames(
              "mt-1 text-sm",
              operationMode === "Offline"
                ? "text-red-500"
                : "text-gray-900 dark:text-white"
            )}
          >
            {operationMode}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CellStatus;

//
// Utils
//

const isRadioActive = (cellHotspot: CellHeartbeat) => {
  if (!cellHotspot || !cellHotspot?.timestamp) return false;

  const activeDate = moment().add(-1, "days");
  const timestamp = moment.unix(cellHotspot.timestamp);

  return (
    cellHotspot?.timestamp &&
    timestamp &&
    moment(timestamp).isAfter(activeDate) &&
    cellHotspot?.operationMode
  );
};
